import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import {
  applyUpdate,
  dismissOfflineReady,
  dismissUpdate,
  getStatus,
  subscribe,
} from "../pwa/registration";
import { CHROME_TOP } from "../app/chrome";
import { useT } from "../i18n";
import { Button } from "./ui";
import { CloudOffIcon, RefreshIcon } from "./icons";

function useOnline(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => {
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
    };
  }, []);
  return online;
}

/**
 * The toast rail: sits above the tab bar and clears the home indicator.
 *
 * Two things live here — "a new version is ready" (the visible half of
 * `registerType: "prompt"`) and the offline indicator, which matters because
 * `/api` is NetworkOnly: offline means genuinely stale, not silently cached.
 */
/**
 * Notices belong at the top, under the header.
 *
 * They used to sit just above the tab bar, which is fine on every screen except
 * the one that has something there: on the chat the "new version" card landed
 * squarely on the composer and swallowed taps meant for the message field. A
 * notice is something to read, not something to reach for, so it moves out of
 * the thumb zone entirely and stops colliding with whatever a screen puts at
 * its own bottom edge.
 */
export function PwaPrompts({ topOffset = 0 }: { topOffset?: number }) {
  const t = useT();
  const status = useSyncExternalStore(subscribe, getStatus, getStatus);
  const online = useOnline();
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!status.offlineReady) return;
    const timer = setTimeout(dismissOfflineReady, 4000);
    return () => clearTimeout(timer);
  }, [status.offlineReady]);

  const show = status.needRefresh || status.offlineReady || !online;

  /*
   * The rail is `fixed`, so it takes no room in the flow and used to sit right
   * on the page's own heading. It reports its height instead, and the layout
   * reserves that much — see `CHROME_TOP`. Measured rather than assumed: the
   * notices stack, and the update card wraps to two lines on a narrow phone.
   */
  const railRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const root = document.documentElement;
    const clear = () => root.style.setProperty(CHROME_TOP, "0px");

    const rail = railRef.current;
    if (!show || !rail) {
      clear();
      return;
    }

    // The 0.5rem above the rail plus the same again below it, so the page does
    // not begin flush against a card.
    const measure = () =>
      root.style.setProperty(
        CHROME_TOP,
        `${Math.ceil(rail.getBoundingClientRect().height) + 16}px`,
      );

    measure();
    if (typeof ResizeObserver === "undefined") return clear;

    const observer = new ResizeObserver(measure);
    observer.observe(rail);
    return () => {
      observer.disconnect();
      clear();
    };
  }, [show, status.needRefresh, status.offlineReady, online]);

  if (!show) return null;

  return (
    <div
      ref={railRef}
      className="pad-safe-x pointer-events-none fixed inset-x-0 z-40 flex flex-col items-center gap-2 px-3"
      style={{ top: `calc(var(--safe-top) + ${topOffset}px + 0.5rem)` }}
    >
      {!online ? (
        <div
          role="status"
          className="pointer-events-auto flex w-full max-w-md items-center gap-2.5 rounded-card bg-warn-soft px-4 py-3 text-[0.875rem] text-warn shadow-soft"
        >
          <CloudOffIcon className="size-5 shrink-0" />
          <span>{t("pwa.offline")}</span>
        </div>
      ) : null}

      {status.offlineReady ? (
        <div
          role="status"
          className="pointer-events-auto flex w-full max-w-md items-center gap-2.5 rounded-card bg-surface px-4 py-3 text-[0.875rem] text-ink-soft shadow-soft"
        >
          <span>{t("pwa.offlineReady")}</span>
        </div>
      ) : null}

      {status.needRefresh ? (
        <div
          role="alert"
          className="pointer-events-auto flex w-full max-w-md items-center gap-3 rounded-card bg-surface px-4 py-3 shadow-soft"
        >
          <RefreshIcon className="size-5 shrink-0 text-accent" />
          <p className="min-w-0 flex-1 text-[0.875rem] text-ink-soft">
            {t("pwa.updateReady")}
          </p>
          <Button
            variant="ghost"
            className="min-h-[var(--touch)] px-3"
            onClick={dismissUpdate}
          >
            {t("pwa.updateLater")}
          </Button>
          <Button
            className="min-h-[var(--touch)] px-3"
            disabled={applying}
            onClick={() => {
              setApplying(true);
              void applyUpdate();
            }}
          >
            {applying ? t("pwa.updating") : t("pwa.update")}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
